import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../providers/auth-provider';
import { getMetadataAttributes } from '../../api/metadata';
import Stack from 'react-bootstrap/Stack';
import { AgGridReact } from 'ag-grid-react';
import {
  getParticipationCompletionStatus,
  getInitialParticipationCompletionStatusModel,
  gridFilter,
} from '../shared/grid/grid-fllters';
import { useNavigate, useParams } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { getCandidateLeaderText } from '../reports/shared/report-util';
import ParticipantDetail from './participant-detail';
import { getClientConfiguration } from '../../api/client';
import LoadingSpinner from '../common/loading-spinner';
import { CELL_CLASS } from '../shared/grid/grid-common';
import { buildMetadataColumn } from '../shared/grid/grid-metadata';
import { FaFileCsv } from 'react-icons/fa';
import { getUserParticipationStatuses } from '../../api/participation';
import { getUserMetrics, isSelectStaffing } from '../../api/dashboards-api';
import { filtersExist, getFilterKeys } from '../shared/filters/filter-common';
import { Container } from 'react-bootstrap';

const defaultColumnDefinition = {
  flex: 1,
  sortable: true,
  wrapHeaderText: true,
};

const gridOptions = {
  suppressCellFocus: true,
};

const getCompletionStatusCellValue = (params) => {
  const participant = params.data;

  if (participant.summary) {
    return participant.completed;
  } else {
    return getParticipationCompletionStatus(participant);
  }
};

const formatRaterPercentage = (params) => {
  return getRaterPercentage(params).toFixed(0) + '%';
};

const getRaterPercentage = (params) => {
  const participant = params.data;

  if (participant.ratersSubmitted) {
    return (participant.ratersSubmitted / participant.ratersAssigned) * 100;
  } else {
    return 0;
  }
};

const group1ColumnDefs = [
  {
    headerName: 'Name',
    field: 'userName',
    valueGetter: (params) => {
      return params.data.userName;
    },
    cellClass: CELL_CLASS,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains', 'equals'],
      suppressAndOrCondition: true,
    },
    sort: 'asc',
    minWidth: 150,
  },
];

const developGroup2ColumnDefs = [
  {
    headerName: 'Self-Assessment Status',
    field: 'completion-status',
    valueGetter: getCompletionStatusCellValue,
    cellClass: CELL_CLASS,
    filter: gridFilter,
    minWidth: 180,
  },
  {
    headerName: 'Raters Nominated',
    field: 'nominated-raters',
    cellClass: CELL_CLASS,
    type: 'numericColumn',
    maxWidth: 150,
    minWidth: 150,
    suppressSizeToFit: true,
    valueGetter: (params) => {
      return params.data.ratersAssigned ? params.data.ratersAssigned : 0;
    },
  },
  {
    headerName: 'Raters Assessments Completed',
    field: 'raters-count-completed',
    cellClass: CELL_CLASS,
    maxWidth: 200,
    minWidth: 180,
    suppressSizeToFit: true,
    valueGetter: (params) => {
      return params.data.ratersSubmitted ? params.data.ratersSubmitted : 0;
    },
  },
  {
    headerName: 'Raters Participation Rate',
    field: 'raters-percent-completed',
    cellClass: CELL_CLASS,
    maxWidth: 200,
    minWidth: 180,
    suppressSizeToFit: true,
    valueFormatter: (params) => formatRaterPercentage(params),
    valueGetter: (params) => getRaterPercentage(params),
  },
];

const selectGroup2ColumnDefs = [
  {
    headerName: 'Status',
    field: 'completion-status',
    valueGetter: getCompletionStatusCellValue,
    cellClass: CELL_CLASS,
    filter: gridFilter,
  },
];

function ParticipationContent() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { filterReference } = useParams();
  const [initialized, setInitialized] = useState(false);
  const [retrievingMetrics, setRetrievingMetrics] = useState(false);
  const [participantStatuses, setParticipantStatuses] = useState([]);
  const [percentAssessmentsComplete, setPercentAssessmentsComplete] = useState('0');
  const [totalRatersAssigned, setTotalRatersAssigned] = useState(0);
  const [totalRatersSubmitted, setTotalRatersSubmitted] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const [showNoMetrics, setShowNoMetrics] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState();
  const [columnDefinitions, setColumnDefinitions] = useState([]);
  const isStaffing = isSelectStaffing();
  const gridRef = useRef();

  const csvExport = () => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsCsv({
        allColumns: true,
        skipPinnedBottom: true,
        fileName: `${auth.slug}-participation.csv`,
      });
    }
  };

  const updateSummaryData = (gridApi) => {
    let nCompleted = 0;
    let nRecords = 0;
    let nRatersAssigned = 0;
    let nRatersSubmitted = 0;
    gridApi.forEachNodeAfterFilter((node) => {
      nRecords += 1;
      if (node.data.assessmentsAssigned === node.data.assessmentsSubmitted) {
        nCompleted += 1;
      }
      if (node.data.ratersAssigned) {
        nRatersAssigned += node.data.ratersAssigned;
        if (node.data.ratersSubmitted) {
          nRatersSubmitted += node.data.ratersSubmitted;
        }
      }
    });
    if (nCompleted > 0) {
      setPercentAssessmentsComplete(((nCompleted / nRecords) * 100).toFixed(0));
    } else {
      setPercentAssessmentsComplete('0');
    }
    setTotalRatersAssigned(nRatersAssigned);
    setTotalRatersSubmitted(nRatersSubmitted);
  };

  const onGridReady = (event) => {
    if (filterReference) {
      event.api.getFilterInstance('completion-status', (filterInstance) => {
        if (filterInstance) {
          filterInstance.setModel(getInitialParticipationCompletionStatusModel(filterReference));
          event.api.onFilterChanged();
        }
      });
    }

    if (filtersExist()) {
      const keys = getFilterKeys();
      keys.forEach((key) => {
        event.api.getFilterInstance(key, (filterInstance) => {
          if (filterInstance) {
            event.api.onFilterChanged();
          }
        });
      });
    }
  };

  const onRowDataUpdated = (event) => {
    updateSummaryData(event.api);
  };

  const onFilterChanged = (event) => {
    if (event.type === 'filterChanged' && event.columns.length > 0) {
      updateSummaryData(event.api);
      const eventKey = event.columns[0].colId;
      if (filtersExist()) {
        const keys = getFilterKeys();
        keys.forEach((key) => {
          if (key !== eventKey) {
            event.api.getFilterInstance(key, (filterInstance) => {
              if (filterInstance) {
                filterInstance.setModel(key);
              }
            });
          }
        });
      }
    }
  };

  useEffect(() => {
    if (!initialized) {
      getUserParticipationStatuses(auth, auth.isDevelop()).then((statuses) => {
        setParticipantStatuses(statuses);
      });
    }
  }, []);

  useEffect(() => {
    if (participantStatuses.length > 0) {
      getMetadataAttributes(auth).then((attributes) => {
        const clientConfiguration = getClientConfiguration();
        const clientMetadata = auth.isDevelop()
          ? clientConfiguration.develop?.participation?.metadata
          : clientConfiguration.select?.participation?.metadata;
        const group2Defs = auth.isDevelop() ? developGroup2ColumnDefs : selectGroup2ColumnDefs;
        if (clientMetadata) {
          const group1Additions = [];
          const group2Additions = [];
          clientMetadata.forEach((spec) => {
            if (spec.group === 1) {
              group1Additions.push(buildMetadataColumn(attributes, spec));
            } else {
              group2Additions.push(buildMetadataColumn(attributes, spec));
            }
          });
          setColumnDefinitions([...group1ColumnDefs, ...group1Additions, ...group2Defs, ...group2Additions]);
        } else {
          setColumnDefinitions([...group1ColumnDefs, ...group2Defs]);
        }
      });
      setInitialized(true);
    }
  }, [participantStatuses]);

  const summaryRowData = useMemo(() => {
    if (auth.isLeader()) {
      return [
        {
          summary: true,
          userName: '',
          completed: `Total Completed: ${percentAssessmentsComplete}%`,
          ratersAssigned: totalRatersAssigned,
          ratersSubmitted: totalRatersSubmitted,
        },
      ];
    } else {
      return [
        {
          summary: true,
          userName: '',
          completed: `Total Completed: ${percentAssessmentsComplete}%`,
        },
      ];
    }
  }, [percentAssessmentsComplete, totalRatersAssigned, totalRatersSubmitted]);

  const onSelectionChanged = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      setRetrievingMetrics(true);
      getUserMetrics(auth, selectedRows[0].userId).then((user) => {
        setRetrievingMetrics(false);
        setSelectedCandidate(user);
        if (user.percentiles) {
          if (isStaffing) {
            navigate(`/reports/${user.reference}`);
          } else {
            setShowDetail(true);
          }
        } else {
          setShowNoMetrics(true);
        }
      });
    }
  };

  return (
    <>
      {(!initialized || retrievingMetrics) && <LoadingSpinner />}
      <Container className="w-100 bg-gray-200" style={{ height: '75vh' }}>
        <Stack className="flex-column p-5 mx-auto pt-4 w-100 overflow-auto h-100">
          <Stack className="chart-card h-100">
            <div className="pt-1 pe-4 d-flex justify-content-end">
              <Button className="bg-transparent border-0" onClick={() => csvExport()}>
                <FaFileCsv size="2em" color="#0f4f73" />
              </Button>
            </div>
            <div className="w-100 h-100">
              <div className="flex-row p-4 pt-1 mx-auto h-100">
                {participantStatuses.length > 0 && columnDefinitions.length > 0 && summaryRowData.length > 0 && (
                  <div className="ag-theme-alpine h-100">
                    <AgGridReact
                      ref={gridRef}
                      onGridReady={onGridReady}
                      rowData={participantStatuses}
                      pinnedBottomRowData={summaryRowData}
                      onRowDataUpdated={onRowDataUpdated}
                      rowSelection={'single'}
                      columnDefs={columnDefinitions}
                      gridOptions={gridOptions}
                      defaultColDef={defaultColumnDefinition}
                      onSelectionChanged={onSelectionChanged}
                      onFilterChanged={onFilterChanged}
                    ></AgGridReact>
                  </div>
                )}
              </div>
            </div>
          </Stack>
        </Stack>
        <ParticipantDetail
          show={showDetail}
          onHide={() => {
            gridRef.current.api.deselectAll();
            setShowDetail(false);
          }}
          candidate={selectedCandidate}
        />
        <Modal
          show={showNoMetrics}
          onHide={() => {
            gridRef.current.api.deselectAll();
            setShowNoMetrics(false);
          }}
        >
          <Modal.Header>
            <Modal.Title>Assessments Not Completed</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              {selectedCandidate ? selectedCandidate.name : ''} has not completed their assigned assessments. Assessment
              reports for a {getCandidateLeaderText(!auth.isLeader())} are not available until they have completed all
              of their assigned assessments.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              onClick={() => {
                gridRef.current.api.deselectAll();
                setShowNoMetrics(false);
              }}
              variant="primary"
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
}

export default ParticipationContent;
